import React, { useEffect, useMemo, useState } from 'react';
import { UserPlus } from 'lucide-react';
import { toast } from 'sonner';

import { facultyExamApi } from '../../api/examApi';
import { parseApiError } from '../../lib/apiErrors';

const TABS = [
  { key: 'students', label: 'Students' },
  { key: 'groups', label: 'Groups' },
  { key: 'everyone', label: 'Everyone' },
];

export default function ExamAssignmentPicker({ examId, assignedStudentIds, onAssigned }) {
  const [tab, setTab] = useState('students');

  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [studentSearch, setStudentSearch] = useState('');
  const [selectedStudentIds, setSelectedStudentIds] = useState(new Set());

  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [selectedGroupIds, setSelectedGroupIds] = useState(new Set());

  const [everyoneOn, setEveryoneOn] = useState(false);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    facultyExamApi.listStudents()
      .then((res) => setStudents(res.data))
      .catch(() => toast.error('Failed to load students'))
      .finally(() => setStudentsLoading(false));
    facultyExamApi.listGroups()
      .then((res) => setGroups(res.data))
      .catch(() => toast.error('Failed to load groups'))
      .finally(() => setGroupsLoading(false));
  }, [examId]);

  const filteredStudents = useMemo(() => {
    const term = studentSearch.trim().toLowerCase();
    if (!term) return students;
    return students.filter(
      (student) => student.name.toLowerCase().includes(term) || student.email.toLowerCase().includes(term)
    );
  }, [students, studentSearch]);

  const toggleStudent = (studentId) => {
    setSelectedStudentIds((current) => {
      const next = new Set(current);
      if (next.has(studentId)) next.delete(studentId);
      else next.add(studentId);
      return next;
    });
  };

  const toggleGroup = (groupId) => {
    setSelectedGroupIds((current) => {
      const next = new Set(current);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const handleEveryoneToggle = () => {
    setEveryoneOn((current) => !current);
  };

  const canSubmit = everyoneOn || selectedStudentIds.size > 0 || selectedGroupIds.size > 0;

  const summaryText = everyoneOn
    ? 'All approved students selected'
    : `${selectedStudentIds.size} student(s), ${selectedGroupIds.size} group(s) selected`;

  const handleAssign = async () => {
    if (!canSubmit) return;
    setAssigning(true);
    try {
      const payload = everyoneOn
        ? { student_ids: [], group_ids: [], all: true }
        : { student_ids: [...selectedStudentIds], group_ids: [...selectedGroupIds], all: false };
      const { data } = await facultyExamApi.assignTargets(examId, payload);
      const alreadyPart = data.already_assigned_count > 0
        ? ` (${data.already_assigned_count} already assigned)`
        : '';
      toast.success(
        `${data.requested_count} selected, ${data.created_count} new assignment(s) created${alreadyPart}`
      );
      setSelectedStudentIds(new Set());
      setSelectedGroupIds(new Set());
      setEveryoneOn(false);
      onAssigned();
    } catch (error) {
      toast.error(parseApiError(error, 'Failed to assign students').message);
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div data-testid="assignment-picker">
      <div className="mb-4 flex gap-1 rounded-md border border-slate-200 p-1 dark:border-slate-700">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            data-testid={`assignment-tab-${t.key}`}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              tab === t.key
                ? 'bg-primary text-white'
                : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'students' && (
        <div className={everyoneOn ? 'pointer-events-none opacity-50' : ''}>
          <input
            type="text"
            placeholder="Search students by name or email"
            value={studentSearch}
            onChange={(event) => setStudentSearch(event.target.value)}
            disabled={everyoneOn}
            data-testid="assignment-student-search"
            className="mb-3 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:ring-2 focus:ring-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
          />
          <div className="max-h-72 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2 dark:border-slate-700">
            {studentsLoading ? (
              <p className="p-2 text-sm text-slate-500 dark:text-slate-400">Loading students…</p>
            ) : filteredStudents.length === 0 ? (
              <p className="p-2 text-sm text-slate-500 dark:text-slate-400">No approved students found.</p>
            ) : (
              filteredStudents.map((student) => {
                const isAssigned = assignedStudentIds.has(student.id);
                return (
                  <label
                    key={student.id}
                    data-testid={`assignment-student-${student.id}`}
                    className={`flex items-center gap-2 rounded-md p-2 text-sm ${
                      isAssigned ? 'text-slate-400' : 'text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700/50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      disabled={isAssigned || everyoneOn}
                      checked={isAssigned || selectedStudentIds.has(student.id)}
                      onChange={() => toggleStudent(student.id)}
                    />
                    <span>
                      {student.name} ({student.email})
                      {isAssigned && ' — already assigned'}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}

      {tab === 'groups' && (
        <div className={everyoneOn ? 'pointer-events-none opacity-50' : ''}>
          <div className="max-h-72 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2 dark:border-slate-700">
            {groupsLoading ? (
              <p className="p-2 text-sm text-slate-500 dark:text-slate-400">Loading groups…</p>
            ) : groups.length === 0 ? (
              <p className="p-2 text-sm text-slate-500 dark:text-slate-400">No groups created yet.</p>
            ) : (
              groups.map((group) => (
                <label
                  key={group.id}
                  data-testid={`assignment-group-${group.id}`}
                  className="flex items-center gap-2 rounded-md p-2 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700/50"
                >
                  <input
                    type="checkbox"
                    disabled={everyoneOn}
                    checked={selectedGroupIds.has(group.id)}
                    onChange={() => toggleGroup(group.id)}
                  />
                  <span>
                    {group.name} ({group.member_count} member{group.member_count === 1 ? '' : 's'})
                  </span>
                </label>
              ))
            )}
          </div>
        </div>
      )}

      {tab === 'everyone' && (
        <div className="rounded-md border border-slate-200 p-4 dark:border-slate-700">
          <label className="flex items-center gap-3 text-sm text-slate-700 dark:text-slate-300">
            <input
              type="checkbox"
              checked={everyoneOn}
              onChange={handleEveryoneToggle}
              data-testid="assignment-everyone-toggle"
            />
            Assign to all approved students
          </label>
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            Turning this on disables the Students and Groups tabs — every currently approved student will be
            targeted.
          </p>
        </div>
      )}

      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="assignment-summary">
          {summaryText}
        </p>
        <button
          onClick={handleAssign}
          disabled={!canSubmit || assigning}
          data-testid="assignment-submit"
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-light disabled:opacity-50"
        >
          <UserPlus size={16} />
          {assigning ? 'Assigning...' : 'Assign Selected'}
        </button>
      </div>
    </div>
  );
}
